import z from 'zod';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[0-9]{1,15}$/;

const contactSchema = z.object({
    name: z.string(),
    email: z.string(),
    mobile: z.string(),
    isPrimaryContact: z.boolean(),
});

const nominatedContactSchema = z.object({
    name: z.string(),
    email: z.string(),
    mobile: z.string(),
    isPrimaryContact: z.boolean(),
    isEnabled: z.boolean(),
}).superRefine((data, ctx) => {
    if (data.isEnabled) {
        if (data.name.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Name is required',
                path: ['name'],
            });
        }
        if (data.email.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Email is required',
                path: ['email'],
            });
        } else if (!emailRegex.test(data.email)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid email address',
                path: ['email'],
            });
        }
        if (data.mobile.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Mobile is required',
                path: ['mobile'],
            });
        } else if (!phoneRegex.test(data.mobile)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please enter a valid mobile number',
                path: ['mobile'],
            });
        }
    }
});

const oasResultSchema = z.object({
    oasMetadataId: z.string(),
    date: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    technicianId: z.string(),
});

const oasBookingSchema = z.object({
    propertyId: z.number().int().positive('Requested property has not been found'),
    bookingTypes: z.string().min(1, 'Please select a service'),
    contacts: z.array(contactSchema),
    nominatedContact: (nominatedContactSchema),
    oasResult: oasResultSchema,
}).superRefine((data, ctx) => {
    const { nominatedContact, contacts } = data;

    const hasNominatedContactData = nominatedContact.email !== ''
            && nominatedContact.mobile !== '' && nominatedContact.name !== '';

    // If all fields are provided, contacts[] can be empty
    if (nominatedContact.isEnabled && hasNominatedContactData) {
        return;
    }

    // if primary contact data is missing or empty
    if (contacts.length > 0 && contacts.some((contact) => contact.isPrimaryContact && (contact.mobile === ''))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The selected primary contact has no existing mobile number. Please select another or nominate a contact for this booking',
            path: ['contacts'],
        });
    }

    if (contacts.length > 0 && contacts.some((contact) => (contact.mobile === ''))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please remove the contact without a mobile number or nominate a new contact',
            path: ['contacts'],
        });
    }

    if (contacts.length > 0 && contacts.some((contact) => contact.isPrimaryContact && (contact.email === '' || contact.email == null))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The selected primary contact has no existing email. Please select another or nominate a contact for this booking',
            path: ['contacts'],
        });
    }

    if (contacts.length > 0 && contacts.some((contact) => (contact.email === '' || contact.email == null))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please remove the contact without an email or nominate a new contact',
            path: ['contacts'],
        });
    }

    if (contacts.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select at least one recipient',
            path: ['contacts'],
        });
    } else {
        const primaryContacts = contacts.filter((contact) => contact.isPrimaryContact);
        if (primaryContacts.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'There must be at least one primary contact',
                path: ['contacts'],
            });
        } else if (primaryContacts.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'There can only be one primary contact',
                path: ['contacts'],
            });
        }
    }
});

const submitBookingFormSchema = z.object({
    oasBooking: oasBookingSchema,
});

type SubmitBookingFormSchemaValues = z.infer<typeof submitBookingFormSchema>;

export { submitBookingFormSchema, SubmitBookingFormSchemaValues };
